import {
  Document,
  Image,
  Page,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";

import fortressLogo from "../assets/fortress-logo.jpg";
import payQr from "../assets/payqr.png";

import {
  bankDetails,
  companyDetails,
} from "../data/invoiceDefaults";

import {
  formatDateSlash,
  formatPrintTimestamp,
} from "../utils/formatDate";

/*
|--------------------------------------------------------------------------
| Invoice PDF Styles
|--------------------------------------------------------------------------
| These styles mirror the Tailwind-based preview layout as closely as
| possible inside @react-pdf/renderer.
|--------------------------------------------------------------------------
*/

const styles = StyleSheet.create({
  /* ============================================================
     PAGE
     ============================================================ */

  page: {
    size: "A4",

    paddingTop: 32,
    paddingBottom: 32,
    paddingLeft: 38,
    paddingRight: 38,

    fontFamily: "Helvetica",
    fontSize: 9,

    color: "#111111",
    backgroundColor: "#ffffff",
  },

  /* ============================================================
     HEADER
     ============================================================ */

  header: {
    width: "100%",

    flexDirection: "row",
    alignItems: "center",

    justifyContent: "space-between",

    marginBottom: 8,
  },

  logoBox: {
    width: 115,
    flexShrink: 0,

    justifyContent: "center",
    alignItems: "flex-start",
  },

  logo: {
    width: 100,
    height: 58,

    objectFit: "contain",
  },

  companyHeading: {
    flex: 1,

    minWidth: 0,

    alignItems: "center",

    paddingLeft: 16,
    paddingRight: 16,
  },

  companyName: {
    fontSize: 16,
    fontWeight: "bold",

    textAlign: "center",

    marginBottom: 5,
  },

  companyAddress: {
    alignItems: "center",

    fontSize: 8,

    lineHeight: 1.4,

    textAlign: "center",
  },

  companyAddressLine: {
    fontSize: 8,
    lineHeight: 1.4,

    textAlign: "center",
  },

  headerQrArea: {
    width: 130,
    height: 130,

    flexShrink: 0,

    justifyContent: "center",
    alignItems: "center",
  },

  headerQr: {
    width: 130,
    height: 130,

    objectFit: "contain",
  },

  goldRule: {
    width: "100%",

    height: 2,

    backgroundColor: "#b58a2a",

    marginBottom: 18,
  },

  /* ============================================================
     INTRO
     ============================================================ */

  intro: {
    position: "relative",

    minHeight: 135,

    width: "100%",
  },

  billTo: {
    width: "48%",
  },

  billingHeading: {
    fontSize: 9,

    fontWeight: "bold",

    marginBottom: 8,
  },

  clientText: {
    fontSize: 9,

    marginBottom: 4,

    lineHeight: 1.35,
  },

  volumeLine: {
    fontSize: 8,

    fontWeight: "bold",

    marginTop: 4,
  },

  invoiceMeta: {
    position: "absolute",

    right: 0,
    top: 0,

    width: "43%",

    textAlign: "right",

    alignItems: "flex-end",
  },

  invoiceMetaText: {
    fontSize: 8,

    marginBottom: 6,

    textAlign: "right",
  },

  invoiceNumber: {
    fontSize: 9,

    marginBottom: 18,

    textAlign: "right",
  },

  billingFromHeading: {
    fontSize: 9,

    fontWeight: "bold",

    marginBottom: 6,

    textAlign: "right",
  },

  billingFrom: {
    fontSize: 8,

    lineHeight: 1.4,

    textAlign: "right",
  },

  invoiceTitle: {
    position: "absolute",

    left: 0,
    bottom: 0,

    fontSize: 17,

    fontWeight: "bold",
  },

  /* ============================================================
     CONTINUATION
     ============================================================ */

  continuationHeading: {
    fontSize: 13,

    fontWeight: "bold",

    marginBottom: 14,
  },

  /* ============================================================
     ITEMS TABLE
     ============================================================ */

  table: {
    width: "100%",

    borderWidth: 1,
    borderColor: "#222222",
  },

  tableHeader: {
    flexDirection: "row",

    backgroundColor: "#eeeeee",

    borderBottomWidth: 1,
    borderBottomColor: "#222222",
  },

  tableRow: {
    flexDirection: "row",

    minHeight: 28,

    borderBottomWidth: 1,
    borderBottomColor: "#cccccc",
  },

  tableRowLast: {
    flexDirection: "row",

    minHeight: 28,
  },

  cell: {
    paddingTop: 6,
    paddingBottom: 6,
    paddingLeft: 5,
    paddingRight: 5,

    borderRightWidth: 1,
    borderRightColor: "#cccccc",

    justifyContent: "center",
  },

  cellLast: {
    paddingTop: 6,
    paddingBottom: 6,
    paddingLeft: 5,
    paddingRight: 5,

    justifyContent: "center",
  },

  descriptionCell: {
    width: "50%",
  },

  rateCell: {
    width: "16%",

    textAlign: "right",
  },

  quantityCell: {
    width: "14%",

    textAlign: "right",
  },

  unitCell: {
    width: "10%",
  },

  amountCell: {
    width: "10%",

    textAlign: "right",
  },

  headerText: {
    fontSize: 8,

    fontWeight: "bold",
  },

  bodyText: {
    fontSize: 8,

    lineHeight: 1.25,
  },

  /* ============================================================
     TOTALS
     ============================================================ */

  totals: {
    marginTop: 12,

    marginLeft: "55%",

    width: "45%",

    borderWidth: 1,
    borderColor: "#222222",
  },

  totalRow: {
    flexDirection: "row",

    justifyContent: "space-between",

    alignItems: "center",

    paddingTop: 8,
    paddingBottom: 8,
    paddingLeft: 8,
    paddingRight: 8,
  },

  totalLabel: {
    fontSize: 9,

    fontWeight: "bold",
  },

  totalAmount: {
    fontSize: 10,

    fontWeight: "bold",
  },

  /* ============================================================
     TERMS
     ============================================================ */

  terms: {
    marginTop: 22,
  },

  sectionHeading: {
    fontSize: 10,

    fontWeight: "bold",

    marginBottom: 8,
  },

  termsText: {
    fontSize: 8,

    lineHeight: 1.45,

    marginBottom: 8,
  },

  boldText: {
    fontWeight: "bold",
  },

  /* ============================================================
     SETTLEMENT
     ============================================================ */

  settlement: {
    marginTop: 22,

    flexDirection: "row",

    alignItems: "flex-start",

    width: "100%",
  },

  bankArea: {
    flex: 1,

    paddingRight: 15,
  },

  /* ============================================================
     BANK TABLE
     ============================================================ */

  bankTable: {
    width: "100%",

    borderWidth: 1,
    borderColor: "#222222",
  },

  bankRow: {
    flexDirection: "row",

    borderBottomWidth: 1,
    borderBottomColor: "#cccccc",
  },

  bankRowLast: {
    flexDirection: "row",
  },

  bankLabel: {
    width: "42%",

    paddingTop: 5,
    paddingBottom: 5,
    paddingLeft: 5,
    paddingRight: 5,

    fontSize: 7,

    fontWeight: "bold",

    borderRightWidth: 1,
    borderRightColor: "#cccccc",
  },

  bankValue: {
    flex: 1,

    paddingTop: 5,
    paddingBottom: 5,
    paddingLeft: 5,
    paddingRight: 5,

    fontSize: 7,
  },

  /* ============================================================
     SIGNATURE
     ============================================================ */

  signature: {
    marginTop: 25,
  },

  signatureLabel: {
    fontSize: 7,

    marginBottom: 18,
  },

  signatureLine: {
    borderBottomWidth: 1,

    borderBottomColor: "#111111",

    marginBottom: 5,
  },

  signatureTitle: {
    fontSize: 7,

    fontWeight: "bold",
  },

  printedDate: {
    marginTop: 15,

    fontSize: 7,
  },

  /* ============================================================
     PAYMENT QR
     ============================================================ */

  /*
   * Mirrors the preview:
   *
   * marginTop: "-50px"
   * width: "160px"
   * height: "160px"
   */

  qrArea: {
    width: 160,

    flexShrink: 0,

    alignItems: "center",

    marginTop: -50,
  },

  qr: {
    width: 100,

    height: 100,

    objectFit: "contain",
  },

  /* ============================================================
     FOOTER
     ============================================================ */

  footerBand: {
    width: "100%",

    height: 12,

    marginTop: 25,

    backgroundColor: "#b58a2a",
  },
});

/* ================================================================
   HELPERS
   ================================================================ */

const renderMultiline = (value) => {
  return String(value || "")
    .split("\n")
    .filter(Boolean)
    .map((line, index) => (
      <Text key={`${line}-${index}`}>{line}</Text>
    ));
};

const chunkItems = (items, limit = 8) => {
  const chunks = [];

  for (let index = 0; index < items.length; index += limit) {
    chunks.push({
      items: items.slice(index, index + limit),
      startIndex: index,
    });
  }

  return chunks.length
    ? chunks
    : [{ items: [], startIndex: 0 }];
};

/* ================================================================
   HEADER
   ================================================================ */

function Header({ qrDataUrl }) {
  return (
    <>
      <View style={styles.header}>

        {/* LEFT — Fortress logo */}
        <View style={styles.logoBox}>
          <Image
            src={fortressLogo}
            style={styles.logo}
          />
        </View>

        {/* CENTER — Company information */}
        <View style={styles.companyHeading}>

          <Text style={styles.companyName}>
            {companyDetails.name}
          </Text>

          <View style={styles.companyAddress}>

            <Text style={styles.companyAddressLine}>
              {companyDetails.address}
            </Text>

            <Text style={styles.companyAddressLine}>
              Email: {companyDetails.email}
            </Text>

            <Text style={styles.companyAddressLine}>
              Website: {companyDetails.website}
            </Text>

            <Text style={styles.companyAddressLine}>
              Tel: {companyDetails.telephone}
            </Text>

          </View>
        </View>

        {/* RIGHT — Invoice QR */}
        {qrDataUrl ? (
          <View style={styles.headerQrArea}>
            <Image
              src={qrDataUrl}
              style={styles.headerQr}
            />
          </View>
        ) : null}

      </View>

      <View style={styles.goldRule} />
    </>
  );
}

/* ================================================================
   INTRO
   ================================================================ */

function Intro({ invoice }) {
  return (
    <View style={styles.intro}>

      {/* BILLING TO */}
      <View style={styles.billTo}>

        <Text style={styles.billingHeading}>
          B I L L I N G  T O :
        </Text>

        <Text style={styles.clientText}>
          {invoice.client.name || " "}
        </Text>

        {invoice.client.address ? (
          <Text style={styles.clientText}>
            {renderMultiline(invoice.client.address)}
          </Text>
        ) : null}

        {invoice.client.phone ? (
          <Text style={styles.clientText}>
            Phone: {invoice.client.phone}
          </Text>
        ) : null}

        {invoice.client.email ? (
          <Text style={styles.clientText}>
            Email: {invoice.client.email}
          </Text>
        ) : null}

        {invoice.client.reference ? (
          <Text style={styles.clientText}>
            Reference: {invoice.client.reference}
          </Text>
        ) : null}

        {invoice.client.volumeWeight ? (
          <Text style={styles.volumeLine}>
            VOLUME/WEIGHT: {invoice.client.volumeWeight}
          </Text>
        ) : null}

      </View>

      {/* RIGHT — Invoice metadata */}
      <View style={styles.invoiceMeta}>

        <Text style={styles.invoiceMetaText}>
          ISSUE DATE: {formatDateSlash(invoice.info.issueDate)}
        </Text>

        <Text style={styles.invoiceNumber}>
          INVOICE NO.: {invoice.info.invoiceNumber}
        </Text>

        <Text style={styles.billingFromHeading}>
          B I L L I N G  F R O M
        </Text>

        <Text style={styles.billingFrom}>
          {renderMultiline(companyDetails.billingFrom)}
        </Text>

      </View>

      {/* INVOICE TITLE */}
      <Text style={styles.invoiceTitle}>
        INVOICE:
      </Text>

    </View>
  );
}

/* ================================================================
   ITEMS TABLE
   ================================================================ */

function ItemsTable({
  invoice,
  items,
  startIndex,
}) {
  const totalRows = items.map((item, index) => {
    const quantity = Number(item.quantity) || 0;
    const rate = Number(item.rate) || 0;

    const amount = quantity * rate;

    return {
      ...item,

      rowNumber: startIndex + index + 1,

      amount,
    };
  });

  return (
    <View style={styles.table}>

      {/* TABLE HEADER */}
      <View style={styles.tableHeader}>

        <View
          style={[
            styles.cell,
            styles.descriptionCell,
          ]}
        >
          <Text style={styles.headerText}>
            DESCRIPTION
          </Text>
        </View>

        <View
          style={[
            styles.cell,
            styles.rateCell,
          ]}
        >
          <Text style={styles.headerText}>
            RATE
          </Text>
        </View>

        <View
          style={[
            styles.cell,
            styles.quantityCell,
          ]}
        >
          <Text style={styles.headerText}>
            QTY
          </Text>
        </View>

        <View
          style={[
            styles.cell,
            styles.unitCell,
          ]}
        >
          <Text style={styles.headerText}>
            UNIT
          </Text>
        </View>

        <View
          style={[
            styles.cellLast,
            styles.amountCell,
          ]}
        >
          <Text style={styles.headerText}>
            AMOUNT
          </Text>
        </View>

      </View>

      {/* TABLE ROWS */}
      {totalRows.map((item, index) => {
        const isLast =
          index === totalRows.length - 1;

        return (
          <View
            key={item.id}
            style={
              isLast
                ? styles.tableRowLast
                : styles.tableRow
            }
          >

            <View
              style={[
                styles.cell,
                styles.descriptionCell,
              ]}
            >
              <Text style={styles.bodyText}>
                {item.description || " "}
              </Text>
            </View>

            <View
              style={[
                styles.cell,
                styles.rateCell,
              ]}
            >
              <Text style={styles.bodyText}>
                {item.rate || "0"}
              </Text>
            </View>

            <View
              style={[
                styles.cell,
                styles.quantityCell,
              ]}
            >
              <Text style={styles.bodyText}>
                {item.quantity || "0"}
              </Text>
            </View>

            <View
              style={[
                styles.cell,
                styles.unitCell,
              ]}
            >
              <Text style={styles.bodyText}>
                {item.unit || " "}
              </Text>
            </View>

            <View
              style={[
                styles.cellLast,
                styles.amountCell,
              ]}
            >
              <Text style={styles.bodyText}>
                {item.amount.toFixed(2)}
              </Text>
            </View>

          </View>
        );
      })}

    </View>
  );
}

/* ================================================================
   TOTALS
   ================================================================ */

function Totals({ invoice }) {
  const subtotal = invoice.items.reduce(
    (total, item) => {
      const quantity =
        Number(item.quantity) || 0;

      const rate =
        Number(item.rate) || 0;

      return (
        total +
        quantity * rate
      );
    },
    0,
  );

  return (
    <View style={styles.totals}>

      <View style={styles.totalRow}>

        <Text style={styles.totalLabel}>
          TOTAL
        </Text>

        <Text style={styles.totalAmount}>
          {invoice.info.currencySymbol}{" "}
          {subtotal.toFixed(2)}
        </Text>

      </View>

    </View>
  );
}

/* ================================================================
   TERMS
   ================================================================ */

function Terms({ invoice }) {
  return (
    <View style={styles.terms}>

      <Text style={styles.sectionHeading}>
        Terms:
      </Text>

      <Text style={styles.termsText}>
        <Text style={styles.boldText}>
          Currency:
        </Text>{" "}
        {invoice.terms.currency}
      </Text>

      <Text style={styles.termsText}>
        <Text style={styles.boldText}>
          Payment:
        </Text>{" "}
        {invoice.terms.payment}
      </Text>

    </View>
  );
}

/* ================================================================
   SETTLEMENT
   ================================================================ */

function Settlement({ invoice }) {
  const printTimestamp =
    formatPrintTimestamp(
      invoice.print.printedAt,
    );

  return (
    <View style={styles.settlement}>

      {/* LEFT — Bank details */}
      <View style={styles.bankArea}>

        <Text style={styles.sectionHeading}>
          BANK ACCOUNTS DETAILS:
        </Text>

        <View style={styles.bankTable}>

          {bankDetails.map(
            ([label, value], index) => {
              const isLast =
                index ===
                bankDetails.length - 1;

              return (
                <View
                  key={label}
                  style={
                    isLast
                      ? styles.bankRowLast
                      : styles.bankRow
                  }
                >

                  <Text style={styles.bankLabel}>
                    {label}
                  </Text>

                  <Text style={styles.bankValue}>
                    {value}
                  </Text>

                </View>
              );
            },
          )}

        </View>

        {/* Signature */}
        <View style={styles.signature}>

          <Text style={styles.signatureLabel}>
            AUTHORIZED SIGNATURE:
          </Text>

          <View style={styles.signatureLine} />

          <Text style={styles.signatureTitle}>
            FINANCE &amp; ACCOUNTS MANAGER
          </Text>

        </View>

        {/* Printed date */}
        {printTimestamp ? (
          <Text style={styles.printedDate}>
            PRINTED DATE &amp; TIME:{" "}
            {printTimestamp}
          </Text>
        ) : null}

      </View>

      {/* RIGHT — Payment QR */}
      <View style={styles.qrArea}>

        <Image
          src={payQr}
          style={styles.qr}
        />

      </View>

    </View>
  );
}

/* ================================================================
   INVOICE PAGE
   ================================================================ */

function InvoicePage({
  invoice,
  items,
  startIndex,
  showIntro,
  showTotal,
  qrDataUrl,
  showSettlement,
  continuation,
}) {
  return (
    <Page
      size="A4"
      style={styles.page}
    >

      <Header
        qrDataUrl={
          showIntro
            ? qrDataUrl
            : null
        }
      />

      {showIntro ? (
        <Intro invoice={invoice} />
      ) : null}

      {continuation ? (
        <Text
          style={styles.continuationHeading}
        >
          Invoice items continued
        </Text>
      ) : null}

      <ItemsTable
        invoice={invoice}
        items={items}
        startIndex={startIndex}
      />

      {showTotal ? (
        <Totals invoice={invoice} />
      ) : null}

      {showSettlement ? (
        <>
          <Terms invoice={invoice} />

          <Settlement invoice={invoice} />

          <View
            style={styles.footerBand}
          />
        </>
      ) : null}

    </Page>
  );
}

/* ================================================================
   SETTLEMENT PAGE
   ================================================================ */

function SettlementPage({
  invoice,
  qrDataUrl,
}) {
  return (
    <Page
      size="A4"
      style={styles.page}
    >

      <Header
        qrDataUrl={qrDataUrl}
      />

      <Text
        style={styles.continuationHeading}
      >
        Invoice settlement
      </Text>

      <Totals
        invoice={invoice}
      />

      <Terms
        invoice={invoice}
      />

      <Settlement
        invoice={invoice}
      />

      <View
        style={styles.footerBand}
      />

    </Page>
  );
}

/* ================================================================
   MAIN PDF
   ================================================================ */

export default function InvoicePDF({
  invoice,
  qrDataUrl,
}) {
  const canUseSinglePage =
    invoice.items.length <= 2;

  const itemChunks =
    chunkItems(invoice.items);

  return (
    <Document
      title={`Invoice ${invoice.info.invoiceNumber}`}
      author={companyDetails.name}
      subject="Invoice"
    >

      {/* ========================================================
          SINGLE PAGE
          ======================================================== */}

      {canUseSinglePage ? (
        <InvoicePage
          invoice={invoice}
          items={invoice.items}
          startIndex={0}
          showIntro
          showTotal
          showSettlement
          qrDataUrl={qrDataUrl}
        />
      ) : (
        <>
          {/* ======================================================
              ITEM PAGES
              ====================================================== */}

          {itemChunks.map(
            (chunk, index) => (
              <InvoicePage
                key={`${chunk.startIndex}-${chunk.items.length}`}
                invoice={invoice}
                items={chunk.items}
                startIndex={chunk.startIndex}
                showIntro={
                  index === 0
                }
                showTotal={
                  index ===
                  itemChunks.length - 1
                }
                continuation={
                  index > 0
                }
                qrDataUrl={
                  qrDataUrl
                }
              />
            ),
          )}

          {/* ====================================================
              SETTLEMENT PAGE
              ==================================================== */}

          <SettlementPage
            invoice={invoice}
            qrDataUrl={qrDataUrl}
          />
        </>
      )}

    </Document>
  );
}